import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { IconTrash } from './Icons';

/* Gestione obiettivi/priorità (form + lista). Priorità UNIFICATA in un solo "modo":
   Alta/Media/Bassa (peso relativo) oppure Programmata (finestra da→a: il sistema
   calcola la potenza per centrare il target entro la fine e spinge di più avvicinandosi). */

const MODI = [
  { v: 'alta', label: 'Alta' },
  { v: 'media', label: 'Media' },
  { v: 'bassa', label: 'Bassa' },
  { v: 'programmata', label: 'Programmata' },
];
const MODO_LABEL = { alta: 'ALTA', media: 'MEDIA', bassa: 'BASSA', programmata: 'PROG' };
const iso = (d) => d.toISOString().slice(0, 10);

function fmtOre(h) {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;
  return `${(h / 24).toFixed(1)} giorni`;
}

function calcEta(ob, velOra) {
  const rim = ob.rimanenti != null ? ob.rimanenti : Math.max(0, (ob.target || 0) - (ob.trovati || 0));
  if (ob.stato === 'raggiunto' || rim <= 0) return { done: true };
  const resa = ob.resa;
  if (!resa || resa <= 0) return { unknown: true };
  const verifiche = Math.ceil(rim / resa);
  const raggiungibile = verifiche <= (ob.da_verificare || 0);
  const oreEta = velOra > 0 ? verifiche / velOra : null;
  return { verifiche, raggiungibile, oreEta };
}

export default function ObiettiviManager() {
  const [obs, setObs] = useState([]);
  const [limite, setLimite] = useState(null);
  const [attivi, setAttivi] = useState(0);
  const [aree, setAree] = useState({ regioni: [], province: [] });
  const [velOra, setVelOra] = useState(0);
  const [tipo, setTipo] = useState('provincia');
  const [nome, setNome] = useState('');
  const [target, setTarget] = useState('');
  const [modo, setModo] = useState('media');
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [tipoImpresa, setTipoImpresa] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/obiettivi`).then((r) => setObs(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    axios.get(`${API}/dashboard/aree-counts`).then((r) => setAree({ regioni: r.data?.regioni || [], province: r.data?.province || [] })).catch(() => {});
    axios.get(`${API}/dashboard/kpi`).then((r) => setVelOra(r.data?.velocita_ora || 0)).catch(() => {});
    axios.get(`${API}/agent/status`).then((r) => {
      setLimite(r.data?.obiettivi_limite ?? null);
      setAttivi(r.data?.obiettivi_attivi ?? 0);
    }).catch(() => {});
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const crea = async () => {
    if (!nome) { toast.error("Scegli un'area"); return; }
    if (!target || Number(target) < 1) { toast.error('Imposta un target valido'); return; }
    if (modo === 'programmata' && !dataFine) { toast.error('Programmata: scegli la data di fine'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/obiettivi`, {
        tipo, nome, target: Number(target), modo,
        data_inizio: modo === 'programmata' ? (dataInizio || null) : null,
        data_fine: modo === 'programmata' ? dataFine : null,
        tipo_impresa: tipoImpresa || '',
      });
      toast.success('Obiettivo creato');
      setNome(''); setTarget(''); setModo('media'); setDataInizio(''); setDataFine(''); setTipoImpresa('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setSaving(false);
  };

  const patch = async (id, body) => {
    try { await axios.patch(`${API}/obiettivi/${id}`, body); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  // Cambio modo da una riga: se passo a Programmata senza finestra, imposto oggi → +7 giorni.
  const cambiaModo = (ob, m) => {
    if (m === 'programmata' && !ob.data_fine) {
      patch(ob.id, { modo: 'programmata', data_inizio: iso(new Date()), data_fine: iso(new Date(Date.now() + 7 * 86400000)) });
    } else {
      patch(ob.id, { modo: m });
    }
  };
  const elimina = async (id) => { try { await axios.delete(`${API}/obiettivi/${id}`); load(); } catch { toast.error('Errore'); } };
  const sposta = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= obs.length) return;
    const ids = obs.map((o) => o.id);
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await axios.put(`${API}/obiettivi/reorder`, { ids }); load(); } catch { toast.error('Errore'); }
  };

  const pieno = limite != null && attivi >= limite;
  const opts = tipo === 'regione' ? aree.regioni : aree.province;

  return (
    <>
      <div className="ob-vel">
        Gli obiettivi guidano l'agente 456 (ordine · priorità · finestra · stop-al-target).
        Velocità attuale: <b>~{velOra.toLocaleString('it-IT')}/ora</b>
      </div>

      <div className="ob-new">
        <select value={tipo} onChange={(e) => { setTipo(e.target.value); setNome(''); }}>
          <option value="provincia">Provincia</option>
          <option value="regione">Regione</option>
        </select>
        <select value={nome} onChange={(e) => setNome(e.target.value)}>
          <option value="">Area…</option>
          {opts.map((o) => <option key={o.nome} value={o.nome}>{o.nome} ({o.count})</option>)}
        </select>
        <input type="number" min={1} placeholder="Target NON TIM" value={target} onChange={(e) => setTarget(e.target.value)} style={{ width: 130 }} />
        <select value={tipoImpresa} onChange={(e) => setTipoImpresa(e.target.value)} title="Tipo impresa">
          <option value="">Tutti</option><option value="azienda">Aziende</option><option value="privato">Privati</option>
        </select>
        <label className="ob-peso">Priorità
          <select value={modo} onChange={(e) => setModo(e.target.value)}>
            {MODI.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </label>
        {modo === 'programmata' && (
          <>
            <label className="ob-peso">Dal<input type="date" value={dataInizio} onChange={(e) => setDataInizio(e.target.value)} title="Inizio (vuoto = subito)" /></label>
            <label className="ob-peso">Entro<input type="date" value={dataFine} onChange={(e) => setDataFine(e.target.value)} title="Fine finestra (deadline)" /></label>
          </>
        )}
        <button className="btn btn-primary" disabled={saving || pieno} onClick={crea}>+ Aggiungi</button>
        <span style={{ fontSize: 11, color: pieno ? '#b45309' : 'var(--muted)' }}>
          Priorità {attivi}/{limite ?? '—'} (linee attive){pieno ? ' · limite raggiunto, rimuovi un obiettivo raggiunto' : ''}
        </span>
      </div>

      <div className="ob-list">
        {obs.length === 0 && <div className="ob-empty">Nessun obiettivo. Aggiungine uno: l'agente lavorerà quelle aree fino al target.</div>}
        {obs.map((ob, i) => {
          const pct = ob.target ? Math.min(100, Math.round((ob.trovati / ob.target) * 100)) : 0;
          const eta = calcEta(ob, velOra);
          const raggiunto = ob.stato === 'raggiunto';
          const sospeso = ob.stato === 'sospeso';
          const prog = ob.modo === 'programmata';
          // programmata: a questo ritmo finisco dopo la data di fine?
          const fuoriTempo = prog && !raggiunto && ob.giorni_a_fine != null && eta.oreEta != null && eta.oreEta > (ob.giorni_a_fine + 1) * 24;
          return (
            <div key={ob.id} className={`ob-item${raggiunto ? ' done' : ''}${sospeso ? ' susp' : ''}`}>
              <div className="ob-ord">
                <button onClick={() => sposta(i, -1)} disabled={i === 0}>▲</button>
                <span>{i + 1}</span>
                <button onClick={() => sposta(i, 1)} disabled={i === obs.length - 1}>▼</button>
              </div>
              <div className="ob-main">
                <div className="ob-title">
                  <span className={`ob-tag ${ob.tipo === 'regione' ? 'reg' : 'prov'}`}>{ob.tipo === 'regione' ? 'REG' : 'PRV'}</span>
                  <b>{ob.nome}</b>
                  <span className={`ob-modo m-${ob.modo || 'media'}`}>{MODO_LABEL[ob.modo] || 'MEDIA'}</span>
                  {ob.tipo_impresa && <span className="ob-sub">{ob.tipo_impresa === 'azienda' ? 'Aziende' : 'Privati'}</span>}
                  {prog && (ob.prog_in_ritardo
                    ? <span className="ob-badge susp">IN RITARDO</span>
                    : !ob.prog_iniziata
                      ? <span className="ob-sub">programmata dal {ob.data_inizio}</span>
                      : <span className="ob-sub">entro {ob.data_fine}{ob.giorni_a_fine != null ? ` · ${ob.giorni_a_fine}gg` : ''}</span>)}
                  {raggiunto && <span className="ob-badge done">RAGGIUNTO</span>}
                  {sospeso && <span className="ob-badge susp">SOSPESO</span>}
                </div>
                <div className="ob-bar"><span style={{ width: `${pct}%` }} /></div>
                <div className="ob-meta">
                  {ob.verifica_completa
                    ? <span><b>Verifica completa</b> · {(ob.da_verificare || 0).toLocaleString('it-IT')} da verificare</span>
                    : <span><b>{(ob.trovati || 0).toLocaleString('it-IT')}</b>/{(ob.target || 0).toLocaleString('it-IT')} NON TIM ({pct}%)</span>}
                  {!raggiunto && (
                    eta.unknown ? <span className="ob-warn">resa sconosciuta (verifica qualche numero)</span>
                      : <>
                          <span>~{(eta.verifiche || 0).toLocaleString('it-IT')} chiamate</span>
                          <span>ETA {fmtOre(eta.oreEta)}</span>
                          {!eta.raggiungibile && <span className="ob-warn">pool insufficiente — rifornisci liste</span>}
                          {fuoriTempo && <span className="ob-warn">a questo ritmo non arrivi entro il {ob.data_fine}</span>}
                        </>
                  )}
                  <span className="ob-sub">{(ob.da_verificare || 0).toLocaleString('it-IT')} da verificare</span>
                </div>
              </div>
              <div className="ob-actions">
                <select className="ob-prio-sel" value={ob.modo || 'media'} title="Priorità"
                  onChange={(e) => cambiaModo(ob, e.target.value)}>
                  {MODI.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
                </select>
                {prog && (
                  <>
                    <input type="date" className="ob-date" value={ob.data_inizio || ''} title="Dal"
                      onChange={(e) => patch(ob.id, { data_inizio: e.target.value || null })} />
                    <input type="date" className="ob-date" value={ob.data_fine || ''} title="Entro"
                      onChange={(e) => e.target.value && patch(ob.id, { data_fine: e.target.value })} />
                  </>
                )}
                {!ob.verifica_completa && (
                  <input type="number" min={1} defaultValue={ob.target} title="Target"
                    onBlur={(e) => Number(e.target.value) !== ob.target && patch(ob.id, { target: Number(e.target.value) })} />
                )}
                <button className="ob-btn" title={sospeso ? 'Riattiva' : 'Sospendi'}
                  onClick={() => patch(ob.id, { stato: sospeso ? 'attivo' : 'sospeso' })}>
                  {sospeso ? 'Riattiva' : 'Sospendi'}
                </button>
                <button className="ob-btn del" title="Elimina" onClick={() => elimina(ob.id)}><IconTrash size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
